import React, { useEffect, useState } from 'react';
import { NetworkCalculator as Calc } from '../utils/networkCalculator';

export const NetworkCalculator: React.FC<any> = () => {
  const [ipAddress, setIpAddress] = useState('192.168.1.1');
  const [subnetMask, setSubnetMask] = useState('255.255.255.0');
  const [cidr, setCidr] = useState(24);
  const [result, setResult] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [calculationTimeout, setCalculationTimeout] = useState<NodeJS.Timeout | null>(null);
  const [ipDisplay, setIpDisplay] = useState<string>('192.168.1.1'); // То, что видит пользователь
  const [ipForCalculation, setIpForCalculation] = useState<string>('192.168.1.1'); // То, что используется для расчетов

  const calculateNetwork = () => {
    try {
      setError('');
      setWarnings([]);
      
      // Используем очищенный IP для расчетов
      const calculatedResult = Calc.calculateAll(ipForCalculation, subnetMask);
      setResult(calculatedResult);
      
      // Получаем предупреждения
      const networkWarnings = Calc.getNetworkWarnings(ipForCalculation, subnetMask);
      setWarnings(networkWarnings);
      
    } catch (error) {
      console.error('Calculation error:', error);
      setError(error.message);
      setResult(null);
      setWarnings([]);
    }
  };

  const updateMaskFromCidr = (newCidr: number) => {
    if (newCidr >= 0 && newCidr <= 32) {
      setCidr(newCidr);
      const maskArray = Calc.cidrToMask(newCidr);
      setSubnetMask(Calc.arrayToIp(maskArray));
      setResult(null); // Очищаем результаты
      setError('');   // Очищаем ошибки
      setWarnings([]); // Очищаем предупреждения
    }
  };

  const handleIpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    
    // Автозамена запятых на точки
    if (value.includes(',')) {
      value = value.replace(/,/g, '.');
    }
    
    setIpDisplay(value); // Всегда показываем то, что ввел пользователь
    setResult(null);
    setError('');

    // Ищем слеш в вводе
    const slashIndex = value.indexOf('/');
    if (slashIndex !== -1) {
      const ipPart = value.substring(0, slashIndex);
      const cidrPart = value.substring(slashIndex + 1);
      
      // Если после слеша есть цифры
      if (cidrPart && /^\d{1,2}$/.test(cidrPart)) {
        const cidrValue = parseInt(cidrPart);
        
        // Проверяем валидность CIDR
        if (cidrValue >= 0 && cidrValue <= 32) {
          // Для расчетов используем только IP часть
          setIpForCalculation(ipPart);
          setCidr(cidrValue);
          
          // Автоматически обновляем маску
          const maskArray = Calc.cidrToMask(cidrValue);
          setSubnetMask(Calc.arrayToIp(maskArray));
          
          // Показываем подсказку
          setError(`CIDR автоматически установлен в /${cidrValue}`);
          
          return;
        }
      }
    }
    
    // Если нет слеша или он невалидный, используем весь ввод для расчетов
    setIpForCalculation(value);
  };

  const handleMaskChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;
    setResult(null);
    setError('');

    // Автозамена запятых на точки
    if (value.includes(',')) {
      value = value.replace(/,/g, '.');
    }

    setSubnetMask(value);

    // Если ввод похож на завершенную маску
    if (value.split('.').length === 4) {
      try {
        // Пытаемся определить CIDR из маски
        if (Calc.isValidMask(value)) {
          const cidrValue = Calc.getCidrFromMask(value);
          
          // Если CIDR изменился - обновляем ползунок
          if (cidrValue !== cidr) {
            setCidr(cidrValue);
            setError(`CIDR автоматически обновлен на /${cidrValue}`);
          }
        }
      } catch (error) {
        // Игнорируем ошибки - пользователь еще вводит
      }
    }
  };

  // В начало компонента
  useEffect(() => {
    return () => {
      if (calculationTimeout) {
        clearTimeout(calculationTimeout);
      }
    };
  }, [calculationTimeout]);

  return (
    <div className="container mt-4">
      <div className="row">
        <div className="col-md-6">
          <div className="card">
            <div className="card-header">
              <h5 className="card-title mb-0">Параметры сети</h5>
            </div>
            <div className="card-body">
              <div className="mb-3">
                <label className="form-label">IP адрес:</label>
                <input
                  type="text"
                  className="form-control"
                  value={ipDisplay} // Показываем то, что ввел пользователь
                  onChange={handleIpChange}
                  placeholder="192.168.1.1 или 192.168.1.0/24"
                />
                <div className="form-text">
                  Можно ввести с CIDR: 10.0.0.0/8, 192.168.1.0/24 (запятые автоматически заменяются на точки)
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Маска подсети:</label>
                <input
                  type="text"
                  className="form-control"
                  value={subnetMask}
                  onChange={handleMaskChange}
                  placeholder="255.255.255.0"
                />
                <div className="form-text">
                  Можно ввести маску или CIDR будет определен автоматически
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">CIDR: /{cidr}</label>
                <input
                  type="range"
                  className="form-range"
                  min="0"
                  max="32"
                  value={cidr}
                  onChange={(e) => updateMaskFromCidr(parseInt(e.target.value))}
                />
              </div>

              <button 
                className="btn btn-primary w-100"
                onClick={calculateNetwork}
                type="submit"
              >
                Рассчитать
              </button>

            </div>
          </div>
        </div>

        <div className="col-md-6">
          {/* Показываем предупреждения */}
          {warnings.length > 0 && (
            <div className="alert alert-warning mb-3">
              <h6 className="alert-heading">⚠️ Внимание!</h6>
              {warnings.map((warning, index) => (
                <div key={index}>• {warning}</div>
              ))}
            </div>
          )}

          {/* Показываем ошибки */}
          {error && (
            <div className={`alert ${error.includes('автоматически') ? 'alert-info' : 'alert-danger'} mt-3`} role="alert">
              {error}
            </div>
          )}
          
          {result && (
            <div className="card">
              <div className="card-header">
                <h5 className="card-title mb-0">Результаты расчета</h5>
              </div>
              <div className="card-body">
                <div className="row">
                  <div className="col-6"><strong>IP адрес:</strong></div>
                  <div className="col-6">{result.ipAddress}</div>
                  
                  <div className="col-6"><strong>Маска подсети:</strong></div>
                  <div className="col-6">{result.subnetMask}</div>
                  
                  <div className="col-6"><strong>Адрес сети:</strong></div>
                  <div className="col-6 text-success">{result.networkAddress}</div>
                  
                  <div className="col-6"><strong>Широковещательный адрес:</strong></div>
                  <div className="col-6 text-danger">{result.broadcastAddress}</div>
                  
                  <div className="col-6"><strong>Доступный диапазон:</strong></div>
                  <div className="col-6">{result.firstUsable} - {result.lastUsable}</div>
                  
                  <div className="col-6"><strong>Всего хостов:</strong></div>
                  <div className="col-6">{result.totalHosts}</div>
                  
                  <div className="col-6"><strong>Доступно хостов:</strong></div>
                  <div className="col-6">{result.usableHosts}</div>
                  
                  <div className="col-6"><strong>Wildcard маска:</strong></div>
                  <div className="col-6">{result.wildcardMask}</div>
                  
                  <div className="col-6"><strong>Класс сети:</strong></div>
                  <div className="col-6">{result.networkClass}</div>
                  
                  <div className="col-6"><strong>CIDR нотация:</strong></div>
                  <div className="col-6">/{result.cidr}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};